import {
  Archive,
  BadgeCheck,
  Bell,
  BookOpen,
  Bookmark,
  Box,
  Boxes,
  Briefcase,
  Brush,
  Building2,
  Calendar,
  Camera,
  Car,
  Cat,
  Coffee,
  Compass,
  Crown,
  Diamond,
  Dog,
  Droplet,
  Factory,
  Flag,
  Flame,
  Flower2,
  Folder,
  Gem,
  Gift,
  Globe,
  Hammer,
  Hash,
  Headphones,
  Heart,
  House,
  Image,
  Landmark,
  Laptop,
  Layers,
  Leaf,
  Link,
  Mail,
  MapPin,
  Medal,
  MessageCircle,
  Mic,
  Monitor,
  Moon,
  Mountain,
  Music,
  Newspaper,
  Package,
  Palette,
  PenTool,
  Phone,
  Plane,
  Scissors,
  Settings,
  Ship,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Star,
  Store,
  Sun,
  Tag,
  Tags,
  Target,
  Trees,
  Trophy,
  Tv,
  Umbrella,
  Utensils,
  Users,
  Video,
  Wine,
  Wrench,
  Zap,
  type LucideIcon,
  type LucideProps,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  brandFallbackColor,
  brandFirstChar,
  isBrandIconKey,
  type BrandIconKey,
} from "@/lib/brand-icons";

export type BrandIconProps = {
  name: string;
  iconKey?: string | null;
  color?: string | null;
  className?: string;
  /** Visual size of the badge / icon container */
  size?: "sm" | "md" | "lg";
};

const sizeClass = {
  sm: "size-4 text-[10px]",
  md: "size-5 text-[11px]",
  lg: "size-9 text-base",
} as const;

const iconSizeClass = {
  sm: "size-2.5!",
  md: "size-3!",
  lg: "size-4!",
} as const;

/** Explicit curated map — do not use lucide `icons` export (pulls full catalog). */
const BRAND_LUCIDE_MAP: Record<BrandIconKey, LucideIcon> = {
  archive: Archive,
  "badge-check": BadgeCheck,
  bell: Bell,
  "book-open": BookOpen,
  bookmark: Bookmark,
  box: Box,
  boxes: Boxes,
  briefcase: Briefcase,
  brush: Brush,
  "building-2": Building2,
  calendar: Calendar,
  camera: Camera,
  car: Car,
  cat: Cat,
  coffee: Coffee,
  compass: Compass,
  crown: Crown,
  diamond: Diamond,
  dog: Dog,
  droplet: Droplet,
  factory: Factory,
  flag: Flag,
  flame: Flame,
  "flower-2": Flower2,
  folder: Folder,
  gem: Gem,
  gift: Gift,
  globe: Globe,
  hammer: Hammer,
  hash: Hash,
  headphones: Headphones,
  heart: Heart,
  house: House,
  image: Image,
  landmark: Landmark,
  laptop: Laptop,
  layers: Layers,
  leaf: Leaf,
  link: Link,
  mail: Mail,
  "map-pin": MapPin,
  medal: Medal,
  "message-circle": MessageCircle,
  mic: Mic,
  monitor: Monitor,
  moon: Moon,
  mountain: Mountain,
  music: Music,
  newspaper: Newspaper,
  package: Package,
  palette: Palette,
  "pen-tool": PenTool,
  phone: Phone,
  plane: Plane,
  scissors: Scissors,
  settings: Settings,
  ship: Ship,
  "shopping-bag": ShoppingBag,
  smartphone: Smartphone,
  sparkles: Sparkles,
  star: Star,
  store: Store,
  sun: Sun,
  tag: Tag,
  tags: Tags,
  target: Target,
  trees: Trees,
  trophy: Trophy,
  tv: Tv,
  umbrella: Umbrella,
  utensils: Utensils,
  users: Users,
  video: Video,
  wine: Wine,
  wrench: Wrench,
  zap: Zap,
};

export function resolveLucideIcon(iconKey: string | null | undefined): LucideIcon | null {
  if (!iconKey || !isBrandIconKey(iconKey)) return null;
  return BRAND_LUCIDE_MAP[iconKey] ?? null;
}

export function BrandIcon({
  name,
  iconKey,
  color,
  className,
  size = "md",
}: BrandIconProps) {
  const Icon = resolveLucideIcon(iconKey);
  const accent = color?.trim() || brandFallbackColor(name);

  if (Icon) {
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-md",
          sizeClass[size],
          className,
        )}
        style={{ backgroundColor: `${accent}22`, color: accent }}
        aria-hidden
      >
        <Icon className={iconSizeClass[size]} strokeWidth={2.25} />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md font-semibold leading-none text-white",
        sizeClass[size],
        className,
      )}
      style={{ backgroundColor: accent }}
      aria-hidden
    >
      {brandFirstChar(name)}
    </span>
  );
}

/** Render a Lucide icon by curated key (picker preview). */
export function BrandLucideGlyph({
  iconKey,
  className,
  ...props
}: { iconKey: string } & LucideProps) {
  const Icon = resolveLucideIcon(iconKey);
  if (!Icon) return null;
  return <Icon className={className} {...props} />;
}
